import { Component, ComponentFactoryResolver, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil } from "rxjs";
import { EditableTableActions } from "../generic-integrations/components/common/editable-table/model/editable-table.model";
import { Table } from "primeng/table";
// Model
import { EmailListUrlParamsModel } from "../generic-integrations/models/email-list-url-params.model";
import { EmailListConfigModel } from "./model/email-list.model";
import { BroadcastService, ModalService, ModalSize } from "@cv-invision/core";
// Service
import { EmailListService } from "./service/email-list.service";
// Component
import { IntegrationsUrlParamsModel } from "../generic-integrations/models/integrations-url-params.model";

@Component({
	selector: 'cv-email-list',
	styleUrls: ['./email-list.component.scss'],
	templateUrl: './email-list.component.html',
	providers: [ConfirmationService, MessageService]
})
export class EmailListComponent implements OnInit, OnDestroy {
	public data: any = [];
	public label = 'Email List'
	public modalSize = ModalSize.LG;
	public extraData: any;
	public emailListParams: IntegrationsUrlParamsModel;
	public clonedData: any = [];
	public searchField: string;

	@Input() formBuilderConfig: any;
	@Input() model: any;
	@ViewChild('dt') dataTable: any;

	public defaultModel: any = {
		disabled: false,
		getValue: (field: any, row: any) => {
			return row[field.key];
		}
	}

	private atComponentDestroy: Subject<any> = new Subject<any>();
	public integrationParams = new EmailListUrlParamsModel()
	public integrationExtraData: any = {};
	public integrations: any;

	constructor(
		public confirmationService: ConfirmationService,
		protected service: EmailListService,
		public messageService: MessageService,
		protected modalService: ModalService,
		protected componentFactoryResolver: ComponentFactoryResolver,
		protected broadcast: BroadcastService,
	) { }

	async ngOnInit(): Promise<void> {
		this.searchField = '';
		this.integrationExtraData.editInModal = true;

		this.extraData = {
			editInModal: true,
			isExternalIntegration: true,
			showSelect: false,
			saveAsObject: true
		};

		this.model = EmailListConfigModel;

		this.emailListParams = new IntegrationsUrlParamsModel();
		this.emailListParams.isExternalIntegration = true;
		this.emailListParams.customUrl = 'utils/email-alert-integration/configs?limit=100';
		this.emailListParams.customSaveUrl = 'utils/email-alert-integration/configs';

		this.loadData();
	}

	get cellWidth(): number {
		return 100 / (this.model.length + 1);
	}

	/**
	 * Save the data to the API on delete
	 *
	 * @param row
	 */
	deleteRow(row: any) {
		this.data = this.data.filter((item: { id: any; }) => item.id !== row.id);
		this.service.deleteEmailConfig(row.id)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(response => {
				if (response) {
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: 'Email configuration was deleted successfully.'
					});
					this.loadData();  // Reload the data after deletion
				} else {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to delete email configuration.'
					});
				}
			});
	}

	/**
	 * Show delete confirmation service
	 *
	 * @param row
	 */
	onShowDeleteConfirm(row: any) {
		this.confirmationService.confirm({
			message: 'Are you sure that you want to proceed?',
			header: 'Confirmation',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.deleteRow(row);
			},
			// reject: () => {}
		});
	}

	/**
	 * Get fields for global filter
	 * @returns
	 */
	getGlobalFilters(): Array<any> {
		return this.model.map((field: { key: any }) => field.key);
	}

	/**
	 * Remove changes from cloned array if edit is cancelled
	 * @param row
	 * @param index
	 */
	onRowEditCancel(row: any, index: number) {
		this.data[index] = this.clonedData[row.id];
		delete this.clonedData[row.id];
	}

	/**
	 * Get the data from the service
	 */
	loadData() {
		this.service
			.getData(this.integrationParams)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.data = result.data ? result.data : result;

				this.data = this.data.map((row: any, index: any) => {
					return {
						...{ id: index },
						...row
					}
				});
			});

		this.service
			.getIntegrations()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				const providerOptions = result.map((item: any) => {
					return {
						label: item,
						value: item,
					};
				});
				this.model['formBuilderConfig']['provider']['options'] = providerOptions;
			});
	}

	/**
	 * Send a broadcast message when a ROW action it's triggered
	 *
	 * @param row
	 */
	onRowAction(action: string, row: any): void {
		this.broadcast.broadcast({
			name: EditableTableActions.TABLE_ROW_ACTION,
			event: {
				type: action,
				row
			}
		})
	}

	/**
	 * Trigger the field onChange
	 *
	 * @param $event
	 * @param field
	 * @param row
	 */
	onChange($event: any, field: any, row?: any): void {
		if (field.onChange) {
			field.onChange($event, row, this.model, this.data, this.integrationExtraData);
		}
	}

	/**
	 * Apply Global Filter
	 * @param $event
	 * @param table
	 */
	applyGlobalFilter($event: any, table: Table): void {
		table.filterGlobal($event.target.value, 'contains');
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}